import {createContext, useContext, useEffect, useState} from 'react'

import {useUpdateUserStorageValue} from '@refra/modules/common/hooks/mutations/useUpdateUserStorageValue'
import {useUserStorageValueQuery} from '@refra/modules/common/hooks/queries/useUserStorageValueQuery'
import type {UserRefraSettings} from '@refra/modules/common/types'

const USER_SETTINGS_KEY = 'refra-user-settings'

interface RefraSettingsContext {
  settings: UserRefraSettings
  setSettings: (newSettings: UserRefraSettings) => void
}

const RefraSettingsContext = createContext<RefraSettingsContext | undefined>(undefined)

export const useRefraSettings = () => {
  const context = useContext(RefraSettingsContext)
  if (!context) throw new Error('useRefraSettings is used outside of the RefraSettingsProvider!')
  return context
}

interface RefraSettingsProviderProps {
  children: React.ReactNode
}

export const RefraSettingsProvider: React.FC<RefraSettingsProviderProps> = ({children}) => {
  const {data: userStorageData} = useUserStorageValueQuery({keyIdentifier: USER_SETTINGS_KEY})
  const {mutate: setUserStorageData} = useUpdateUserStorageValue()

  const defaultSettings: UserRefraSettings = {
    displayHotspots: true,
    displayMortar: true,
    useLocalColors: false
  }

  const [settings, setSettings] = useState<UserRefraSettings>(defaultSettings)

  const setUserSettings = (newSettings: UserRefraSettings) => {
    setSettings(newSettings)
    setUserStorageData({
      keyIdentifier: USER_SETTINGS_KEY,
      value: JSON.stringify(newSettings)
    })
  }

  useEffect(() => {
    if (userStorageData && userStorageData.value) {
      const parsedSettings = JSON.parse(userStorageData.value) as UserRefraSettings
      setSettings({...defaultSettings, ...parsedSettings})
    }
  }, [userStorageData])

  return (
    <RefraSettingsContext.Provider value={{settings, setSettings: setUserSettings}}>
      {children}
    </RefraSettingsContext.Provider>
  )
}
